import { useSelector } from "react-redux";
import { useRef, useState, useEffect } from "react";
import produce from "immer";
import * as echarts from "echarts";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import { getBodyFont } from "common/utils/font";
import { IconCourseClear } from "features/user/assets";

enum ChartType {
  TOTAL_CLEARED = "TOTAL_CLEARED",
  NEW_CLEARED = "NEW_CLEARED",
  AVG_TOTAL_CLEARED = "AVG_TOTAL_CLEARED",
}

const generateChartOption = (
  periodSeries: string[], // MM-DD
  totalClearedSeries: number[],
  newClearedSeries: number[],
  newClearedAvgValue?: number,
) => {
  return {
    animation: false,
    grid: {
      left: "3%",
      right: "4%",
      bottom: "120px",
    },
    xAxis: [
      {
        type: "category",
        boundaryGap: true,
        data: periodSeries,
        splitArea: {
          show: true,
          areaStyle: {
            color: ["rgba(148, 196, 247, 0)", "rgba(112, 177, 244, 0.15)"],
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          interval: 0,
          formatter: (value: string) => {
            const date = value.split("-");
            return `{month|${Number(date[0])}/}{day|${date[1]}}`;
          },
          rich: {
            month: {
              fontSize: 12,
              color: "#3F4A61",
              verticalAlign: "top",
              fontFamily: getBodyFont(),
            },
            day: {
              fontSize: 24,
              color: "#3F4A61",
              fontFamily: getBodyFont(),
            },
          },
          show: true,
          margin: 45,
          padding: [18, 0, 0, 0],
          width: 56,
          height: 32,
          backgroundColor: {
            image: `${Constants.assetHost}/assets/images/user_page_axis_label_bg.png`,
          },
        },
      },
    ],
    yAxis: [
      {
        show: false,
        max: function (value) {
          return value.max / 0.7;
        },
        axisTick: { show: false },
      },
      {
        show: false,
        max: function (value) {
          return value.max / 0.7;
        },
        axisTick: { show: false },
      },
      {
        show: false,
        max: function (value) {
          return value.max / 0.2;
        },
        axisTick: { show: false },
      },
    ],
    series: [
      {
        type: "bar",
        barWidth: "98%",
        itemStyle: { color: "#7cc148" },
        data: totalClearedSeries,
      },
      {
        type: "line",
        yAxisIndex: 1,
        data: newClearedSeries,
        lineStyle: { color: "#F9E532", width: 8 },
        label: {
          show: true,
          position: "inside",
          formatter: (params: object) =>
            params["dataIndex"] === newClearedSeries.length - 1
              ? `{latest|${params["value"]}}`
              : `{normal|${params["value"]}}`,
          rich: {
            latest: {
              fontSize: 32,
              color: "white",
              fontFamily: getBodyFont(),
            },
            normal: {
              fontSize: 24,
              color: "white",
              fontFamily: getBodyFont(),
            },
          },
        },
        labelLayout(params: object) {
          if (params["dataIndex"] === newClearedSeries.length - 1) {
            return {
              x: params["labelRect"].x,
              y: params["rect"].y + params["rect"].height / 2 + 5,
            };
          }
        },
        showSymbol: true,
        symbolSize: (value: number, params: object) => {
          if (params["dataIndex"] === newClearedSeries.length - 1) {
            return [90, 90];
          } else {
            return [56, 48];
          }
        },
        symbol: (value: number, params: object) => {
          if (params["dataIndex"] === newClearedSeries.length - 1) {
            return `image://${Constants.assetHost}/assets/images/user_page_course_clear_icon_latest.png`;
          } else {
            return `image://${Constants.assetHost}/assets/images/user_page_course_clear_icon.png`;
          }
        },
      },
      {
        type: "line",
        yAxisIndex: 2,
        lineStyle: { opacity: 0 },
        showSymbol: false,
        markLine: {
          symbol: "none",
          label: { show: false },
          data: [{ type: "max" }],
          lineStyle: {
            width: 12,
            cap: "round",
            type: [0, 20],
            color: "#F9E532",
          },
        },
        data: periodSeries.map(() => newClearedAvgValue),
      },
    ],
  };
};

export const UserProgressCourseChart = ({ selected }: { selected: boolean }) => {
  const play = usePlaySound();
  const ref = useRef<HTMLDivElement>(null);
  const [chartTypes, setChartTypes] = useState<ChartType[]>([
    ChartType.TOTAL_CLEARED,
    ChartType.NEW_CLEARED,
  ]);
  const [chart, _setChart] = useState<echarts.ECharts | null>(null);
  const chartRef = useRef(chart);
  const setChart = (chart: echarts.ECharts) => {
    _setChart(chart);
    chartRef.current = chart;
  };

  const courseAlgorithmSummary = useSelector(
    (state: RootState) => state.courses.progresses.algorithm.summary,
  );
  const courseProgramSummary = useSelector(
    (state: RootState) => state.courses.progresses.program.summary,
  );
  const history = useSelector((state: RootState) => state.user.history);

  useEffect(() => {
    if (chartRef.current) {
      const option = generateChartOption(
        history.period,
        chartTypes.includes(ChartType.TOTAL_CLEARED)
          ? history.course.total.day
              .map((value) => value.finished)
              .concat([courseAlgorithmSummary.finished + courseProgramSummary.finished])
          : [],
        chartTypes.includes(ChartType.NEW_CLEARED)
          ? history.course.new.day
              .map((value) => value.finished)
              .concat([
                courseAlgorithmSummary.finished +
                  courseProgramSummary.finished -
                  history.course.total.day[history.course.total.day.length - 1].finished,
              ])
          : [],
      );
      chartRef.current.setOption(option);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartTypes.length]);

  useEffect(() => {
    if (ref.current && history && !chartRef.current && selected) {
      const chart = echarts.init(ref.current, null, {
        renderer: "svg",
        width: 720,
        height: 600,
      });
      chart.showLoading();
      const option = generateChartOption(
        history.period,
        chartTypes.includes(ChartType.TOTAL_CLEARED)
          ? history.course.total.day
              .map((value) => value.finished)
              .concat([courseAlgorithmSummary.finished + courseProgramSummary.finished])
          : [],
        chartTypes.includes(ChartType.NEW_CLEARED)
          ? history.course.new.day
              .map((value) => value.finished)
              .concat([
                courseAlgorithmSummary.finished +
                  courseProgramSummary.finished -
                  history.course.total.day[history.course.total.day.length - 1].finished,
              ])
          : [],
      );
      chart.setOption(option);
      chart.hideLoading();
      setChart(chart);
    }
    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
      }
      setChart(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current, history, selected]);

  const handleSelectChartType = (chartType: ChartType) => () => {
    play();
    setChartTypes(
      produce((draft) => {
        const targetIndex = draft.findIndex((type) => type === chartType);
        if (targetIndex !== -1) {
          draft.splice(targetIndex, 1);
        } else {
          draft.push(chartType);
        }
      }),
    );
  };

  if (!history) {
    return (
      <div
        className="flex-col-el flex-1 w-full h-full items-start justify-start overflow-hidden"
        style={{ display: selected ? undefined : "none" }}
      >
        <div className="flex-row-el flex-center relative w-full h-full overflow-hidden">
          <img
            alt="ゲームの背景"
            src={`${Constants.assetHost}/assets/images/bg_user_page_history.png`}
            className="w-full h-full absolute top-0 left-0"
          />
          <div className="flex-col-center w-[100px] h-[100px]">
            <div className="dots-loader" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex-col-el flex-1 w-full h-full items-start justify-start overflow-hidden"
      style={{ display: selected ? undefined : "none" }}
    >
      <div className="flex-row-el flex-center relative w-full h-full overflow-hidden">
        <img
          alt="ゲームの背景"
          src={`${Constants.assetHost}/assets/images/bg_user_page_history.png`}
          className="w-full h-full absolute top-0 left-0"
        />

        <div className="flex-row-el flex-center w-[720px] h-[600px] z-10">
          <div ref={ref} className="flex-row-el w-full h-full" />
        </div>

        <div className="flex-col-el flex-center absolute right-4 top-1/2 -translate-y-1/2 gap-4 w-[104px] z-10">
          <div
            className="flex-col-el flex-center relative cursor-pointer transition"
            style={{
              transform: `translateY(${chartTypes.includes(ChartType.TOTAL_CLEARED) ? 3 : 0}px)`,
            }}
            onClick={handleSelectChartType(ChartType.TOTAL_CLEARED)}
          >
            <div className="flex-col-el z-10">
              <IconCourseClear />
            </div>
            <div
              className={`flex-col-el flex-center rounded-full w-[96px] h-[96px] -mt-3 transition ${
                chartTypes.includes(ChartType.TOTAL_CLEARED) ? "bg-yellow-100" : "bg-white"
              }`}
              style={{
                boxShadow: chartTypes.includes(ChartType.TOTAL_CLEARED)
                  ? "0px 3px 3px -3px rgba(0, 0, 0, 0.2), 0px 3px 4px 1px rgba(0, 0, 0, 0.14), 0px 1px 8px 3px rgba(0, 0, 0, 0.12)"
                  : "0px 6px 6px -3px rgba(0, 0, 0, 0.2), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)",
              }}
            >
              <p className="text-body whitespace-pre text-[16px] leading-5">
                {I18n.t("MSG_USER_PROFILE_PROGRESS_COURSE_LEVEL_TITLE")}
              </p>
              <p className="text-body whitespace-pre text-[32px] leading-8">
                {courseAlgorithmSummary.finished + courseProgramSummary.finished}
              </p>
              <p className="text-body whitespace-pre text-[16px] leading-5">
                {I18n.t("MSG_COURSE_GAME_CLEAR")}
              </p>
            </div>
          </div>

          <div
            className="flex-col-el flex-center relative cursor-pointer transition"
            style={{
              transform: `translateY(${chartTypes.includes(ChartType.NEW_CLEARED) ? 3 : 0}px)`,
            }}
            onClick={handleSelectChartType(ChartType.NEW_CLEARED)}
          >
            <div className="flex-col-el z-10">
              <IconCourseClear />
            </div>
            <div
              className={`flex-col-el flex-center text-textcolor-black rounded-full w-[96px] h-[96px] -mt-3 transition ${
                chartTypes.includes(ChartType.NEW_CLEARED) ? "bg-yellow-100" : "bg-white"
              }`}
              style={{
                boxShadow: chartTypes.includes(ChartType.NEW_CLEARED)
                  ? "0px 3px 3px -3px rgba(0, 0, 0, 0.2), 0px 3px 4px 1px rgba(0, 0, 0, 0.14), 0px 1px 8px 3px rgba(0, 0, 0, 0.12)"
                  : "0px 6px 6px -3px rgba(0, 0, 0, 0.2), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)",
              }}
            >
              <p className="text-body whitespace-pre text-[16px] leading-5">
                {I18n.t("MSG_USER_PROFILE_PROGRESS_NEW_TITLE")}
              </p>
              <p className="text-body whitespace-pre text-[32px] leading-8">
                {courseAlgorithmSummary.finished +
                  courseProgramSummary.finished -
                  history.course.total.day[history.course.total.day.length - 1].finished}
              </p>
              <p className="text-body whitespace-pre text-[16px] leading-5">
                {I18n.t("MSG_COURSE_GAME_CLEAR")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
